import {
  EntityType,
  ExactMatchQueryOperators,
  FileQueryOperators,
  MatchQueryOperators,
  QueryOperator,
  VqlIssuesValues,
  VqlMatchOps,
  VqlModeOps,
  VqlStrOps,
  VqlQuery,
  VqlQueryBase,
  FilterType,
  FilterKey,
  CustomMetadataValue,
  VqlCustomMetadataOps,
  CustomMetadataOperators,
  CustomMetadataFieldType,
  Filters,
  SizeFilterOp,
  APIQueryOperators,
  SizeSupportedOperators,
  FilterMenuItemType
} from "types";
import { v4 as uuidv4 } from "uuid";
import {
  LABELS_PARAMETER,
  USER_TAGS_PARAMETER,
  VQL_PARAMETER,
} from "helpers/constants/miscellaneous";
import {isCustomMetadata} from "./customMetadataHelpers";

const matchQueryOperatorToAPIQueryOperators: Record<
  MatchQueryOperators,
  VqlMatchOps
> = {
  [QueryOperator.IS]: "is",
  [QueryOperator.IS_NOT]: "is_not",
  [QueryOperator.IS_ONE_OF]: "one_of",
  [QueryOperator.IS_NOT_ONE_OF]: "not_one_of",
};

const exactMatchQueryOperatorToAPIQueryModes: Record<
  ExactMatchQueryOperators,
  VqlModeOps
> = {
  [QueryOperator.IS]: "in",
  [QueryOperator.IS_NOT]: "out",
};

const fileQueryOperatorToAPIQueryOperators: Record<
  FileQueryOperators,
  VqlStrOps | "one_of"
> = {
  [QueryOperator.IS_ONE_OF]: "one_of",
  [QueryOperator.CONTAINS]: "contains",
  [QueryOperator.STARTS_WITH]: "starts_with",
  [QueryOperator.ENDS_WITH]: "ends_with",
};

const customMetadataOperatorToAPIQueryOperators: Record<
  CustomMetadataOperators,
  VqlCustomMetadataOps
> = {
  [QueryOperator.IS]: "is",
  [QueryOperator.IS_NOT]: "is_not",
  [QueryOperator.IS_ONE_OF]: "one_of",
  [QueryOperator.IS_NOT_ONE_OF]: "not_one_of",
  [QueryOperator.CONTAINS]: "contains",
  [QueryOperator.GREATER_THAN]: "gt",
  [QueryOperator.LESS_THAN]: "lt",
  [QueryOperator.GREATER_THAN_OR_EQUAL_TO]: "gte",
  [QueryOperator.LESS_THAN_OR_EQUAL_TO]: "lte",
  [QueryOperator.BETWEEN]: "between",
};

const getApiQueryOperatorByQueryOperators: Record<
  QueryOperator,
  APIQueryOperators
> = {
  [QueryOperator.IS]: "is",
  [QueryOperator.IS_NOT]: "is_not",
  [QueryOperator.IS_ONE_OF]: "one_of",
  [QueryOperator.IS_NOT_ONE_OF]: "not_one_of",
  [QueryOperator.CONTAINS]: "contains",
  [QueryOperator.GREATER_THAN]: "gt",
  [QueryOperator.LESS_THAN]: "lt",
  [QueryOperator.GREATER_THAN_OR_EQUAL_TO]: "gte",
  [QueryOperator.LESS_THAN_OR_EQUAL_TO]: "lte",
  [QueryOperator.BETWEEN]: "between",
  [QueryOperator.ENDS_WITH]: "ends_with",
  [QueryOperator.STARTS_WITH]: "starts_with",
  [QueryOperator.DOES_NOT_CONTAIN]: "does_not_contain"
};


export const createAndUpdateLabelsFilter = (
  labels: string[],
  operator: MatchQueryOperators,
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  labels: {
    op: matchQueryOperatorToAPIQueryOperators[operator] as VqlMatchOps,
    value: labels,
  },
});

export const createAndUpdateIssueTypesFilter = (
  issueType: Omit<VqlIssuesValues, "duplicates">,
  operator: ExactMatchQueryOperators = QueryOperator.IS,
  confidenceRange: [number, number] = [0, 1],
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  issues: {
    op: "issue",
    value: issueType.toLowerCase() as VqlIssuesValues,
    confidence_min: confidenceRange[0],
    confidence_max: confidenceRange[1],
    mode: exactMatchQueryOperatorToAPIQueryModes[operator] as VqlModeOps,
  },
});

export const createAndUpdateDuplicatesFilter = (
  threshold: number,
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  duplicates: {
    op: "duplicates",
    value: threshold,
  },
});

export const createAndUpdateSizeFilter = (
  size: number,
  operator: SizeSupportedOperators,
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  size: {
    op: getApiQueryOperatorByQueryOperators[operator as CustomMetadataOperators] as SizeFilterOp,
    value: size,
  },
});

export const createAndUpdateUniquenessFilter = (
  threshold: number,
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  uniqueness: { op: "uniqueness", value: threshold },
});

export const createAndUpdateUserTagsFilter = (
  userTags: string[],
  operator: MatchQueryOperators,
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  tags: {
    op: matchQueryOperatorToAPIQueryOperators[operator] as VqlMatchOps,
    value: userTags,
  },
});

export const createAndUpdateSearchTextFilter = (
  searchText: string,
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  text: {
    op: "semantic",
    value: searchText,
  },
});

export const createAndUpdateCaptionFilter = (
  caption: string,
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  text: {
    op: "fts",
    value: caption,
  },
});

export const createAndUpdateEntityTypeFilter = (
  entityType: EntityType,
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  entity_type: {
    op: "is",
    value: entityType,
  },
});

export const createAndUpdateCustomMetadataFilter = (
  fieldId: string,
  fieldName: string,
  fieldType: CustomMetadataFieldType,
  operator: CustomMetadataOperators,
  value: CustomMetadataValue,
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  custom_metadata: {
    field_id: fieldId,
    field_name: fieldName,
    field_type: fieldType,
    op: customMetadataOperatorToAPIQueryOperators[operator],
    value,
  },
});

export const createAndUpdateFileFilter = (
  file: string | string[],
  operator: FileQueryOperators,
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  file: {
    op: fileQueryOperatorToAPIQueryOperators[operator] as
      | VqlMatchOps
      | VqlStrOps,
    value: file,
  },
});

export const createAndUpdateFolderFilter = (
  folder: string | string[],
  operator: FileQueryOperators,
  id?: string
): VqlQuery => ({
  id: id || uuidv4(),
  folder: {
    op: fileQueryOperatorToAPIQueryOperators[operator] as
      | VqlMatchOps
      | VqlStrOps,
    value: folder,
  },
});

export function extractValuesFromVqlQueries(
  vqlQueries: VqlQuery[]
): Record<string, string> {
  const extractedValues: Record<string, string> = {};

  vqlQueries.forEach((query) => {
    // Extract labels with "is" operator
    if ("labels" in query && query.labels && query.labels.op === "is") {
      extractedValues[LABELS_PARAMETER] = JSON.stringify(query.labels.value);
    }

    // Extract tags with "is" operator
    if ("tags" in query && query.tags && query.tags.op === "is") {
      extractedValues[USER_TAGS_PARAMETER] = JSON.stringify(query.tags.value);
    }
  });

  return extractedValues;
}

export const isVqlQuery = <K extends FilterKey>(
  query: VqlQuery,
  key: K
): query is VqlQueryBase & Extract<FilterType, Record<K, any>> => {
  return key in query;
};


const queryMatchersByFilter: Record<Filters, (q: VqlQuery) => boolean> = {
  [Filters.LABELS]: (q) => "labels" in q,
  [Filters.USER_TAGS]: (q) => "tags" in q,
  [Filters.CAPTION]: (q) => "text" in q,
  [Filters.DUPLICATES]: (q) => "duplicates" in q,
  [Filters.FILE]: (q) => "file" in q,
  [Filters.FOLDER]: (q) => "folder" in q,
  [Filters.UNIQUENESS]: (q) => "uniqueness" in q,
  [Filters.ISSUES]: (q) => false,
  [Filters.MISLABELS]: (q) =>
    "issues" in q && q.issues?.value === "mislabels",
  [Filters.OUTLIERS]: (q) =>
    "issues" in q && q.issues?.value === "outliers",
  [Filters.LABEL_OUTLIER]: (q) =>
    "issues" in q && q.issues?.value === "label_outlier",
  [Filters.SIZE]: (q) =>  "size" in q,
};

export const getFilterKeyByFilterType = (
  filterType: FilterMenuItemType
): FilterKey | null => {
  if (isCustomMetadata(filterType)) {
    return filterType.field_name as FilterKey;
  }

  const staticMap: Record<Filters, FilterKey> = {
    [Filters.FILE]: "file",
    [Filters.FOLDER]: "folder",
    [Filters.LABELS]: "labels",
    [Filters.USER_TAGS]: "tags",
    [Filters.CAPTION]: "text",
    [Filters.MISLABELS]: "issues",
    [Filters.LABEL_OUTLIER]: "issues",
    [Filters.DUPLICATES]: "duplicates",
    [Filters.OUTLIERS]: "issues",
    [Filters.ISSUES]: "issues",
    [Filters.UNIQUENESS]: "uniqueness",
    [Filters.SIZE]: "size",
  };

  return staticMap[filterType as Filters] || null;
};



export const getExistingQueryByFilterType = (
  type: Filters,
  queries: VqlQuery[]
): VqlQuery | undefined => {
  const matchedQuery = queryMatchersByFilter[type];
  return queries.find(matchedQuery);
};

const removeFilterFromVqlQuery = (
  searchParams: URLSearchParams,
  filterKey: FilterKey
) => {
  const vqlParam = searchParams.get(VQL_PARAMETER);
  if (vqlParam) {
    const decoded = decodeURIComponent(vqlParam);
    const filters = JSON.parse(decoded);

    const filtered = filters.filter((filter: any) => !(filterKey in filter));

    if (filtered.length === 0) {
      searchParams.delete(VQL_PARAMETER);
    } else {
      searchParams.set(VQL_PARAMETER, JSON.stringify(filtered));
    }
  }
};

export const removeFiltersFromVqlQuery = (
  searchParams: URLSearchParams,
  filterKeys: FilterKey[]
) => {
  filterKeys.forEach((key) => {
    removeFilterFromVqlQuery(searchParams, key);
  });
};
